import React, { useImperativeHandle, useRef, useState } from 'react';
import { COLORS } from '../../constants';
import drawUtils from '../../pages/LessonPage/drawUtils';
import TrySquareDIV from '../icons/TrySquareDIV';
import { DeleteForever } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import VBToolTip from '../Tooltip/VBToolTip';
import { FormattedMessage } from 'react-intl';
import MoveIcon from '../icons/MoveIcon';
import RotateIconNew from '../icons/RotateIconNew';


const TRYSQUARE_WIDTH = 350;
const TRYSQUARE_HEIGHT = 250;

const useStyles = makeStyles(() => ({
    moveButton: {
        position: 'absolute',
        color: COLORS.primaryD2,
        bottom: '32px',
        width: '32px',
        height: '32px',
        left: '40px',
        borderRadius: '50%',
        backgroundColor: COLORS.primaryD2 + 33,
    },
    deleteButton: {
        position: 'absolute',
        color: COLORS.secondaryD2,
        right: '50%',
        bottom: '32px',
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        backgroundColor: COLORS.secondaryD2 + 33,
    },
    rotateButton: {
        position: 'absolute',
        color: COLORS.primaryD2,
        bottom: '32px',
        width: '32px',
        height: '32px',
        right: '4px',
        borderRadius: '50%',
        backgroundColor: COLORS.primaryD2 + 33,
    },
}));

const TrySquareComponent = React.forwardRef((props, ref) => {
    let [visible, setVisible] = useState(false);
    let [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
    let [position, setPosition] = useState({
        x: drawUtils.CANVAS_ACTUAL_WIDTH / 3,
        y: drawUtils.CANVAS_ACTUAL_HEIGHT / 3,
    });
    let [rotation, setRotation] = useState(0);
    let [canHandle, setCanHandle] = useState(false);
    const classes = useStyles();

    let refUpdateDebouncer = useRef({
        position: {
            x: drawUtils.CANVAS_ACTUAL_WIDTH / 3,
            y: drawUtils.CANVAS_ACTUAL_HEIGHT / 3,
        },
        startPoint: { x: 0, y: 0 },
        visible: false,
        debounceFunc: null,
        rotation: 0,
        moveX: 0,
        moveY: 0,
    });

    useImperativeHandle(ref, () => ({
        showElement(el) {
            if (el !== undefined && el !== null) {
                setVisible(el);
                if (refUpdateDebouncer && refUpdateDebouncer.current) {
                    refUpdateDebouncer.current.visible = el;
                }
            } else {
                setVisible(!visible);
                if (refUpdateDebouncer && refUpdateDebouncer.current) {
                    refUpdateDebouncer.current.visible = !visible;
                }
            }

            setTimeout(() => {
                updatePositions(true);
            }, 200);
        },
        updateScale(startPoint) {
            setStartPoint({ x: startPoint.x, y: startPoint.y });
            refUpdateDebouncer.current.startPoint = { x: startPoint.x, y: startPoint.y };
            setTimeout(updatePositions, 200);
        },
        getOverlapPoint(x, y, ignoreToleranceForProjection = false) {
            if (!visible) return null;
            let originEl = document.getElementById('trysquare-origin-point')?.getBoundingClientRect();
            let hEndEl = document.getElementById('trysquare-horizontal-end')?.getBoundingClientRect();
            let vEndEl = document.getElementById('trysquare-vertical-end')?.getBoundingClientRect();
            if (!originEl || !hEndEl || !vEndEl) return null;
            let rect = drawUtils.getCurrentCanvasRect();

            let origin = { x: originEl.left - rect.left, y: originEl.top - rect.top };
            let hEnd = { x: hEndEl.left - rect.left, y: hEndEl.top - rect.top };
            let vEnd = { x: vEndEl.left - rect.left, y: vEndEl.top - rect.top };

            const TOLERANCE = 10;

            if (Math.abs(origin.x - x) < TOLERANCE && Math.abs(origin.y - y) < TOLERANCE) {
                return origin;
            }
            if (Math.abs(hEnd.x - x) < TOLERANCE && Math.abs(hEnd.y - y) < TOLERANCE) {
                return hEnd;
            }
            if (Math.abs(vEnd.x - x) < TOLERANCE && Math.abs(vEnd.y - y) < TOLERANCE) {
                return vEnd;
            }

            let hProj = drawUtils.getProjectionPoint(origin, hEnd, { x, y });
            let hDist = Math.hypot(hProj.x - x, hProj.y - y);
            let vProj = drawUtils.getProjectionPoint(origin, vEnd, { x, y });
            let vDist = Math.hypot(vProj.x - x, vProj.y - y);

            if (ignoreToleranceForProjection) {
                return hDist <= vDist ? hProj : vProj;
            }

            let hWithin = hDist < TOLERANCE;
            let vWithin = vDist < TOLERANCE;

            if (hWithin && vWithin) {
                return hDist <= vDist ? hProj : vProj;
            }
            if (hWithin) return hProj;
            if (vWithin) return vProj;

            return null;
        },
        getPosition() {
            return position;
        },
        getState() {
            return {
                visible: refUpdateDebouncer.current.visible,
                rotation: refUpdateDebouncer.current.rotation,
                position: refUpdateDebouncer.current.position,
            };
        },
        setAllowWriting(canWrite) {
            setCanHandle(canWrite);
            setTimeout(() => {
                updatePositions();
            }, 100);
        },
        updateState(data) {
            if (data) {
                setVisible(data.visible);
                refUpdateDebouncer.current.visible = data.visible;
                if (data.position) {
                    setPosition(data.position);
                    refUpdateDebouncer.current.position = data.position;
                }
                if (data.rotation) {
                    setRotation(data.rotation);
                    refUpdateDebouncer.current.rotation = data.rotation;
                }
            }
        },
    }));

    const updatePositions = (shouldCallProps = false) => {
        if (props.updateState) {
            let state = {
                visible: refUpdateDebouncer.current.visible,
                rotation: refUpdateDebouncer.current.rotation,
                position: refUpdateDebouncer.current.position,
            };
            if (shouldCallProps) {
                props.updateState(state);
            }
        }
    };

    // --- Window globals for DraggerControl in StickyElements ---
    // Exactly follows the ruler pattern: handleStart calculates offset,
    // move/stop use offset to track position.

    window.moveTrySquare = (event, data) => {
        let dx = data.x - refUpdateDebouncer.current.dragStartX;
        let dy = data.y - refUpdateDebouncer.current.dragStartY;
        let origPos = refUpdateDebouncer.current.dragOrigPosition;
        let origScreen = drawUtils.reverseScalePoint(refUpdateDebouncer.current.startPoint, origPos.x, origPos.y);
        if (!origScreen) return;
        let pos = drawUtils.scalePoint(refUpdateDebouncer.current.startPoint, origScreen.x + dx, origScreen.y + dy);

        if (!refUpdateDebouncer.current.debounceFunc) {
            refUpdateDebouncer.current.debounceFunc = setTimeout(() => {
                if (refUpdateDebouncer?.current?.position) {
                    setPosition({ ...refUpdateDebouncer.current.position });
                }
                refUpdateDebouncer.current.debounceFunc = null;
            }, 30);
        }
        refUpdateDebouncer.current.position = pos;
    };

    window.stopTrySquare = (event, data) => {
        let dx = data.x - refUpdateDebouncer.current.dragStartX;
        let dy = data.y - refUpdateDebouncer.current.dragStartY;
        let origPos = refUpdateDebouncer.current.dragOrigPosition;
        let origScreen = drawUtils.reverseScalePoint(refUpdateDebouncer.current.startPoint, origPos.x, origPos.y);
        if (!origScreen) return;
        let pos = drawUtils.scalePoint(refUpdateDebouncer.current.startPoint, origScreen.x + dx, origScreen.y + dy);

        if (refUpdateDebouncer.current.debounceFunc) {
            clearTimeout(refUpdateDebouncer.current.debounceFunc);
            refUpdateDebouncer.current.debounceFunc = null;
        }
        setPosition(pos);
        refUpdateDebouncer.current.position = pos;
        if (props.updateState) {
            let state = {
                visible: refUpdateDebouncer.current.visible,
                rotation: refUpdateDebouncer.current.rotation,
                position: refUpdateDebouncer.current.position,
            };
            props.updateState(state);
        }

        refUpdateDebouncer.current.shouldReposition = true;
    };

    window.rotateTrySquare = (event, data) => {
        if (!refUpdateDebouncer.current.debounceFunc) {
            refUpdateDebouncer.current.debounceFunc = setTimeout(() => {
                setRotation(refUpdateDebouncer.current.rotation);
                refUpdateDebouncer.current.debounceFunc = null;
            }, 30);
        } else {
            let el = document.getElementById('trysquare-origin-point')?.getBoundingClientRect();
            let rect = drawUtils.getCurrentCanvasRect();

            let x1 = data.x + rect.x + refUpdateDebouncer.current.moveX;
            let y1 = data.y + rect.y + refUpdateDebouncer.current.moveY;
            let currentAngle = (Math.atan2(el.y - y1, el.x - x1) * 180) / Math.PI + 180;
            let delta = currentAngle - refUpdateDebouncer.current.startAngle;
            delta = ((delta + 180) % 360 + 360) % 360 - 180;
            refUpdateDebouncer.current.rotation = ((refUpdateDebouncer.current.startRotation + delta) % 360 + 360) % 360;
        }
    };

    window.stopTrySquareRotate = (event, data) => {
        if (refUpdateDebouncer.current.debounceFunc) {
            clearTimeout(refUpdateDebouncer.current.debounceFunc);
            refUpdateDebouncer.current.debounceFunc = null;
        }
        let el = document.getElementById('trysquare-origin-point')?.getBoundingClientRect();
        let rect = drawUtils.getCurrentCanvasRect();

        let x1 = data.x + rect.x + refUpdateDebouncer.current.moveX;
        let y1 = data.y + rect.y + refUpdateDebouncer.current.moveY;
        let currentAngle = (Math.atan2(el.y - y1, el.x - x1) * 180) / Math.PI + 180;
        let delta = currentAngle - refUpdateDebouncer.current.startAngle;
        delta = ((delta + 180) % 360 + 360) % 360 - 180;
        refUpdateDebouncer.current.rotation = ((refUpdateDebouncer.current.startRotation + delta) % 360 + 360) % 360;

        setRotation(refUpdateDebouncer.current.rotation);
        if (props.updateState) {
            let state = {
                visible: refUpdateDebouncer.current.visible,
                rotation: refUpdateDebouncer.current.rotation,
                position: refUpdateDebouncer.current.position,
            };
            props.updateState(state);
        }

        updatePositions();
    };

    window.handleStartTrySquareMove = (event, data) => {
        refUpdateDebouncer.current.dragStartX = data.x;
        refUpdateDebouncer.current.dragStartY = data.y;
        refUpdateDebouncer.current.dragOrigPosition = { ...refUpdateDebouncer.current.position };
    };

    window.handleStartTrySquareRotate = (event, data) => {
        let el = document.getElementById('trysquare-horizontal-end')?.getBoundingClientRect();
        let rect = drawUtils.getCurrentCanvasRect();
        let w = 0;
        let h = 0;
        if (el && rect) {
            let xx = data.x + rect.left;
            let yy = data.y + rect.top;
            w = el.x - xx;
            h = el.y - yy;
        }
        refUpdateDebouncer.current.moveX = w;
        refUpdateDebouncer.current.moveY = h;
        // Store the initial angle and rotation so we can track delta
        let origin = document.getElementById('trysquare-origin-point')?.getBoundingClientRect();
        if (origin && el) {
            let ix = data.x + rect.x + w;
            let iy = data.y + rect.y + h;
            refUpdateDebouncer.current.startAngle = (Math.atan2(origin.y - iy, origin.x - ix) * 180) / Math.PI + 180;
            refUpdateDebouncer.current.startRotation = refUpdateDebouncer.current.rotation;
        }
    };

    window.hideTrySquare = () => {
        setVisible(false);
        refUpdateDebouncer.current.visible = false;

        if (props.updateState) {
            let state = {
                visible: refUpdateDebouncer.current.visible,
                rotation: refUpdateDebouncer.current.rotation,
                position: refUpdateDebouncer.current.position,
            };

            props.updateState(state);
        }
    };

    let normalizedRotation = ((rotation % 360) + 360) % 360;
    let t = 'rotate(' + normalizedRotation + 'deg)';
    let rotateVal = Number.parseFloat(normalizedRotation).toFixed(1);
    if (rotateVal == 360 || rotateVal == 0) {
        rotateVal = 0;
    } else {
        rotateVal = Number.parseFloat(360 - normalizedRotation).toFixed(1);
    }
    if (!position) {
        return null;
    }
    let rswhWH = drawUtils.reverseScalePoint({ x: 0, y: 0 }, TRYSQUARE_WIDTH, TRYSQUARE_HEIGHT);

    if (!rswhWH) return null;

    let reverseScaledPoint = drawUtils.reverseScalePoint(startPoint, position.x, position.y);
    if (!reverseScaledPoint) return null;
    let px = reverseScaledPoint.x;
    let py = reverseScaledPoint.y;
    return (
        <div>
            <div
                style={{
                    transform: t,
                    width: rswhWH.x + 'px',
                    height: rswhWH.y + 'px',
                    position: 'absolute',
                    left: px + 'px',
                    top: py + 'px',
                    transformOrigin: '0% 100%',
                    display: visible ? 'block' : 'none',
                }}
            >
                <div
                    id='trysquare-start-point'
                    style={{
                        position: 'absolute',
                        left: '0px',
                        top: '0px',
                    }}
                />
                <div
                    id='trysquare-origin-point'
                    style={{
                        position: 'absolute',
                        left: '0px',
                        bottom: '0px',
                    }}
                />
                <div
                    id='trysquare-horizontal-end'
                    style={{
                        position: 'absolute',
                        right: '0px',
                        bottom: '0px',
                    }}
                />
                <div
                    id='trysquare-vertical-end'
                    style={{
                        position: 'absolute',
                        left: '0px',
                        top: '0px',
                    }}
                />
                <TrySquareDIV />
                <RotateIconNew
                    className={classes.rotateButton}
                    id='trysquare-rotate-icon-position'
                    style={{ visibility: canHandle ? 'visible' : 'hidden' }}
                />
                <DeleteForever
                    className={classes.deleteButton}
                    id='trysquare-delete-icon-position'
                    onClick={() => {
                        setVisible(false);
                    }}
                    onTouchStart={() => {
                        setVisible(false);
                    }}
                    style={{ visibility: canHandle ? 'visible' : 'hidden' }}
                />
                <MoveIcon
                    className={classes.moveButton}
                    id='trysquare-move-icon-position'
                    style={{ visibility: canHandle ? 'visible' : 'hidden' }}
                />
                {rotateVal ? (
                    <VBToolTip content={<FormattedMessage id='tooltip.lessonPage.angle' />}>
                        <div
                            style={{
                                position: 'absolute',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontSize: '0.7rem',
                                transform: `rotate(${rotateVal}deg)`,
                                width: '36px',
                                height: '36px',
                                fontWeight: '700',
                                color: COLORS.primaryD1,
                                backgroundColor: COLORS.primary + '66',
                                borderRadius: '50%',
                                bottom: '32px',
                                left: '0px',
                            }}
                        >
                            {rotateVal}
                        </div>
                    </VBToolTip>
                ) : null}
            </div>
        </div>
    );
});

export default TrySquareComponent;
